import { useId, useLayoutEffect } from "react";
import { type ThemeId, themes } from "../../domain/theme";

/**
 * Themes requested by currently mounted components, in mount order. Map keeps
 * insertion order on update, so a component changing its theme does not jump
 * ahead of components mounted after it.
 */
const claims = new Map<string, ThemeId>();

/**
 * Colors the browser UI (e.g. the mobile status bar) to match the theme. The
 * entrypoint HTML has no theme-color meta tag, so it is removed when no theme
 * is claimed to restore the default behavior.
 */
function setThemeColorMeta(color: string | null) {
  let meta = document.head.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  );

  if (!color) {
    meta?.remove();
    return;
  }

  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.append(meta);
  }

  meta.content = color;
}

function applyTopmostClaim() {
  const theme = [...claims.values()].at(-1);
  const root = document.documentElement;

  if (theme) {
    root.dataset.theme = theme;
  } else {
    delete root.dataset.theme;
  }

  setThemeColorMeta(theme ? themes[theme].background : null);
}

/**
 * Applies a theme to the whole document (including portaled dialogs) for as
 * long as the calling component is mounted. When multiple components claim a
 * theme, the most recently mounted one wins. Passing `null` releases the claim.
 */
export function useDocumentTheme(theme: ThemeId | null) {
  const id = useId();

  useLayoutEffect(() => {
    if (theme) {
      claims.set(id, theme);
    } else {
      claims.delete(id);
    }

    applyTopmostClaim();
  }, [id, theme]);

  useLayoutEffect(() => {
    return () => {
      claims.delete(id);
      applyTopmostClaim();
    };
  }, [id]);
}
